import React from 'react';
import { claimChunkReload } from './chunkReloadGuard';

/**
 * Run a dynamic import; on failure reload the page (stale chunk after a deploy) unless a chunk
 * reload already happened within the cooldown window. In that case rethrow the original error so
 * it propagates to the nearest error boundary instead of looping.
 */
export function importWithChunkRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().catch((error) => {
    if (claimChunkReload()) {
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw error;
  });
}

/**
 * Drop-in replacement for React.lazy that handles stale chunks after deploys.
 *
 * When Render deploys a new container, old chunk filenames no longer exist.
 * Browsers with a cached index.html will 404 on the old chunk and get HTML back,
 * which fails the dynamic import. A full reload fetches the new index.html with
 * correct chunk hashes.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(importFn: () => Promise<{ default: T }>) {
  return React.lazy(() => importWithChunkRetry(importFn));
}
